import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, cloneElement, isValidElement, useId, } from 'react';
import { cx } from './cx.js';
const DEFAULT_LABEL_CLASS = 'block font-medium text-rr-fg-primary mb-1';
/** Elements the field can wire `aria-invalid` / `aria-describedby` onto itself. */
const CONTROL_TAGS = new Set(['input', 'select', 'textarea']);
/**
 * F-05: when the field wraps exactly one native control, give it
 * `aria-invalid` (while there is an error) and `aria-describedby` (the
 * message id). Anything else (a component, a group of radios, a nested
 * checkbox label) is returned untouched; those consumers wire it themselves.
 */
function wireControl(children, invalid, messageId) {
    const nodes = Children.toArray(children);
    if (nodes.length !== 1)
        return children;
    const only = nodes[0];
    if (!isValidElement(only) || typeof only.type !== 'string')
        return children;
    if (!CONTROL_TAGS.has(only.type))
        return children;
    const own = only.props['aria-describedby'];
    const describedBy = [own, messageId].filter((v) => typeof v === 'string' && v !== '').join(' ');
    return cloneElement(only, {
        'aria-invalid': only.props['aria-invalid'] ?? (invalid ? true : undefined),
        'aria-describedby': describedBy === '' ? undefined : describedBy,
    });
}
/** Small "!" in a circle, inherits the message color. Decorative. */
function ErrorIcon() {
    return (_jsxs("svg", { "aria-hidden": "true", focusable: "false", "data-rr-field-error-icon": "", viewBox: "0 0 16 16", width: "14", height: "14", className: "mt-px shrink-0", fill: "none", stroke: "currentColor", strokeWidth: "1.6", children: [_jsx("circle", { cx: "8", cy: "8", r: "6.5" }), _jsx("path", { d: "M8 4.5v4.25", strokeLinecap: "round" }), _jsx("circle", { cx: "8", cy: "11.25", r: "0.6", fill: "currentColor", stroke: "none" })] }));
}
/**
 * F-05: the one look for a field's error line: warning icon, field danger
 * color, `role="alert"` so it is announced when it appears. RrFormField and
 * the RrInput / RrSelect / RrTextArea primitives all render their error
 * through this, so every form in the product shows errors the same way.
 */
export function RrFieldError({ id, children }) {
    return (_jsxs("span", { id: id, role: "alert", "data-rr-field-error": "", className: "mt-1 flex items-start gap-1 text-xs font-medium text-rr-fg-danger", children: [_jsx(ErrorIcon, {}), children] }));
}
export function RrFormField({ label, required = false, htmlFor, helpText, error, children, className, labelClassName, }) {
    const autoId = useId();
    const hasError = error !== undefined && error !== null && error !== '' && error !== false;
    const hasHelp = !hasError && helpText !== undefined && helpText !== null && helpText !== '' && helpText !== false;
    // Stable id for the help/error line, wired to the control's
    // `aria-describedby`. Derived from `htmlFor` when present (deterministic,
    // matches the consumer's existing `${id}-error` conventions), else auto.
    const messageId = htmlFor !== undefined ? `${htmlFor}-message` : `${autoId}-message`;
    const labelSpan = (_jsxs("span", { className: cx(labelClassName ?? DEFAULT_LABEL_CLASS), children: [label, required ? _jsx("span", { className: "text-rr-state-danger ml-0.5", children: "*" }) : null] }));
    const message = hasError ? (_jsx(RrFieldError, { id: messageId, children: error })) : hasHelp ? (_jsx("span", { id: messageId, className: "block mt-1 text-xs text-rr-fg-secondary", children: helpText })) : null;
    const control = wireControl(children, hasError, message !== null ? messageId : undefined);
    // Sibling-mode: explicit htmlFor label + control as a sibling, in a <div>.
    if (htmlFor !== undefined) {
        return (_jsxs("div", { className: cx('text-sm', className), children: [_jsxs("label", { htmlFor: htmlFor, className: cx(labelClassName ?? DEFAULT_LABEL_CLASS), children: [label, required ? _jsx("span", { className: "text-rr-state-danger ml-0.5", children: "*" }) : null] }), control, message] }));
    }
    // Wrap-mode: the <label> wraps the control (implicit association); the
    // help/error line follows it, linked by `aria-describedby`.
    return (_jsxs("div", { className: cx('block text-sm', className), children: [_jsxs("label", { className: "block", children: [labelSpan, control] }), message] }));
}
//# sourceMappingURL=RrFormField.js.map